import { useEffect, useRef, useState } from "react";

/**
 * Cuadro de diálogo para pedir el motivo de rechazo.
 *
 * Reemplaza `window.prompt()`, que tenía dos problemas reales: algunos
 * navegadores lo bloquean (sobre todo tras un await, como ya pasó con
 * window.open en esta app — ver utils/abrirEnNuevaPestana.ts), y da una
 * caja de una sola línea sin formato para un texto que queda registrado en
 * la auditoría y lo lee otra persona.
 *
 * Usa <dialog> nativo: el navegador se encarga del backdrop, del foco
 * atrapado dentro del diálogo y de cerrar con Escape.
 */
interface Props {
  abierto: boolean;
  titulo: string;
  /** Contexto de qué se está rechazando, para no rechazar la fila equivocada. */
  detalle?: string;
  onConfirmar: (motivo: string) => void;
  onCancelar: () => void;
}

export function ModalMotivo({ abierto, titulo, detalle, onConfirmar, onCancelar }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (abierto && !dialog.open) {
      setMotivo(""); // nunca arrastrar el motivo de un rechazo anterior
      dialog.showModal();
    } else if (!abierto && dialog.open) {
      dialog.close();
    }
  }, [abierto]);

  if (!abierto) return null;

  const motivoLimpio = motivo.trim();

  return (
    <dialog ref={dialogRef} className="modal" onCancel={onCancelar}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (motivoLimpio) onConfirmar(motivoLimpio);
        }}
      >
        <h2>{titulo}</h2>
        {detalle && <p className="modal-detalle">{detalle}</p>}

        <textarea
          autoFocus
          value={motivo}
          placeholder="Motivo del rechazo (queda registrado en la auditoria)..."
          onChange={(e) => setMotivo(e.target.value)}
        />

        <div className="modal-acciones">
          <button type="button" className="btn-ghost" onClick={onCancelar}>
            Cancelar
          </button>
          {/* Sin motivo no se puede rechazar: es el dato que despues explica
              por que se rechazo, y el backend lo exige. */}
          <button type="submit" className="btn-danger" disabled={!motivoLimpio}>
            Rechazar
          </button>
        </div>
      </form>
    </dialog>
  );
}
